/**
 * Tug-E-Nuff Predictive Search — ten-search.js (v5, Horizon 4.1.4)
 * -----------------------------------------------------------------------------
 * The old ten-search.js (25 Jun – 6 Jul 2026) ran its own /search/suggest.json
 * fetch, its own debounce, and its own tabbed dropdown, anchored to the OLD
 * header markup (#ten-search-bar, #ten-search-modal, form.search, etc).
 * Horizon 4.1.4 replaced the header search with Shopify's own
 * dialog-component + predictive-search-component, which already fetches,
 * debounces, handles keyboard nav, and renders results — none of the old
 * anchor elements exist any more, so that file has been a silent no-op since
 * the upgrade.
 *
 * Rather than re-fight the new component (and re-break on the theme's next
 * update), this file leaves Shopify's native search doing the fetching,
 * debouncing and rendering, and layers TEN's own behaviour on top:
 *   1. Toys / Collections / Articles tabs over the live results — by tagging
 *      Shopify's own result sections and toggling `hidden`, never by moving
 *      or rebuilding them, so morph-based re-rendering keeps working.
 *   2. Click-to-search wiring for the "Popular searches" links in the
 *      default panel (snippets/ten-search-default-panel.liquid).
 *   3. GA4 dropdown-search tracking, ported as-is from the old file's GA4
 *      block (6 Jul 2026) — Shopify's own Search & Discovery report only
 *      counts the /search results page and excludes dropdown activity, so
 *      without this most real searches never show up in Analytics.
 *
 * Visual branding (the orange border, bold input, hidden prices, tabs,
 * default-panel columns) lives in the paired assets/ten-search.css — both
 * are included from snippets/search-modal.liquid.
 */
(function () {
  'use strict';

  const CONFIG = {
    trackMinChars: 3,      // ignore very short/incidental queries
    trackDelay:    1000,   // ms of typing-idle before a settled query logs
    ga4Id:        'G-MZK3Q8CWDD',
  };

  // Tab definitions, in default-priority order (first non-empty group wins
  // unless the shopper has already picked a tab that's still non-empty).
  // Chris Nelis-Rouse — round 1 split Collections into its own tab since
  // that's what the "Toys / Collections / Articles" labels seemed to
  // promise; the original pre-4.1.4 design actually folded collections in
  // with the toy results under a single Toys tab, and that's the design
  // being restored here — collections tag alongside products so they show
  // together, leaving just Toys / Articles.
  const TAB_GROUPS = [
    { key: 'toys', label: 'Toys', selectors: ['#predictive-search-products', '#predictive-search-collection'] },
    { key: 'articles', label: 'Articles', selectors: ['#predictive-search-article', '#predictive-search-page'] },
  ];

  function init() {
    const modal = document.getElementById('search-modal');
    if (!modal) return;

    const input = modal.querySelector('input.search-input');
    if (!input) return;

    setupResultTabs(modal);
    setupInstantSearchLinks(modal, input);

    let trackTimer;
    const sentSearches = new Set();

    // Query fresh each time — Shopify's morph() patches this subtree on every
    // keystroke, so a cached reference can go stale.
    function resultsRoot() {
      return modal.querySelector('[data-search-results]');
    }
    function countResults() {
      const root = resultsRoot();
      return root ? root.querySelectorAll('[ref="resultsItems[]"]').length : 0;
    }
    function hasNoResultsMessage() {
      const root = resultsRoot();
      return !!root && !!root.querySelector('.predictive-search-results__no-results');
    }

    function trackSearchQuery(term) {
      const q = (term || '').trim();
      if (q.length < CONFIG.trackMinChars) return;
      const key = q.toLowerCase();
      if (sentSearches.has(key)) return;
      sentSearches.add(key);
      try {
        if (typeof window.gtag !== 'function') return;
        const resultsCount = countResults();
        window.gtag('event', 'search', {
          search_term:    q,
          search_results: resultsCount,        // 0 = a search that returned nothing
          search_source:  'ten_predictive',     // separates dropdown from /search hits
          send_to:        CONFIG.ga4Id,         // GA4 only — keep it out of Ads
        });
        if (resultsCount === 0 && hasNoResultsMessage()) {
          window.gtag('event', 'search_no_results', {
            search_term:   q,
            search_source: 'ten_predictive',
            send_to:       CONFIG.ga4Id,
          });
        }
      } catch (e) { /* analytics must never break search */ }
    }

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(trackTimer);
      if (!q) { sentSearches.clear(); return; }
      trackTimer = setTimeout(() => trackSearchQuery(q), CONFIG.trackDelay);
    });

    // A fast tap on a result can beat the idle timer — flush immediately.
    // Delegated on the outer dialog so it survives morph() re-rendering the
    // results underneath it.
    modal.addEventListener('click', (e) => {
      if (e.target && e.target.closest && e.target.closest('a')) {
        clearTimeout(trackTimer);
        trackSearchQuery(input.value);
      }
    }, true);
  }

  /**
   * Builds/updates the Toys / Collections / Articles tab bar over the live
   * results. Runs on every DOM change inside the results pane (Shopify's
   * morph() rewrites it on every keystroke), tags whichever result sections
   * are present with [data-ten-tab-group], and shows only the active one.
   * Never moves or recreates Shopify's own markup — only tags/hides it —
   * so its slideshow-component carousels, keyboard nav, etc. keep working.
   */
  function setupResultTabs(modal) {
    const content = modal.querySelector('.predictive-search-form__content');
    if (!content) return;

    const observer = new MutationObserver(schedule);
    let scheduled = false;

    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; organize(); });
    }

    function topLevelChild(inner, selector) {
      const el = inner.querySelector(selector);
      if (!el) return null;
      let node = el;
      while (node.parentElement && node.parentElement !== inner) node = node.parentElement;
      return node.parentElement === inner ? node : null;
    }

    function activateTab(inner, key) {
      inner.dataset.tenActiveTab = key;
      inner.querySelectorAll(':scope > .ten-search-tabs .ten-search-tab').forEach((b) => {
        b.classList.toggle('ten-search-tab--active', b.dataset.tab === key);
      });
      inner.querySelectorAll('[data-ten-tab-group]').forEach((el) => {
        el.hidden = el.dataset.tenTabGroup !== key;
      });
    }

    function organize() {
      const inner = content.querySelector('.predictive-search-results__inner');
      const bar = content.querySelector('.ten-search-tabs');
      if (!inner) { if (bar) bar.remove(); return; } // default panel, or nothing rendered yet

      // Disconnect while we write, so our own edits don't re-trigger this
      // observer and spin forever.
      observer.disconnect();
      try {
        const tabs = TAB_GROUPS
          .map((def) => ({
            ...def,
            els: def.selectors.map((sel) => topLevelChild(inner, sel)).filter(Boolean),
          }))
          .filter((def) => def.els.length > 0);

        // Clear stale tagging from a previous pass (e.g. a group that had
        // results before and doesn't now) before re-tagging.
        inner.querySelectorAll('[data-ten-tab-group]').forEach((el) => {
          delete el.dataset.tenTabGroup;
          el.hidden = false;
        });

        const existingBar = inner.querySelector(':scope > .ten-search-tabs');
        if (tabs.length <= 1) {
          if (existingBar) existingBar.remove();
          delete inner.dataset.tenActiveTab;
          return;
        }

        tabs.forEach((t) => t.els.forEach((el) => { el.dataset.tenTabGroup = t.key; }));

        // Chris Nelis-Rouse — the merged Toys tab should read collection(s)
        // first, then products underneath, matching the pre-4.1.4 layout.
        // Shopify always renders the products section before the collections
        // carousel in the underlying markup, so without this the collection
        // would land at the bottom of the tab instead. This is the one place
        // we actually reorder (not just tag/hide) Shopify's own nodes — safe
        // because we're only swapping the order of two already-rendered
        // top-level siblings, not touching what's inside either of them, so
        // the next morph() still finds everything where it expects.
        const toysGroup = tabs.find((t) => t.key === 'toys');
        if (toysGroup && toysGroup.els.length > 1) {
          const productsEl = topLevelChild(inner, '#predictive-search-products');
          const collectionEl = topLevelChild(inner, '#predictive-search-collection');
          if (productsEl && collectionEl && collectionEl.nextElementSibling !== productsEl) {
            inner.insertBefore(collectionEl, productsEl);
          }
          if (collectionEl) toysGroup.els = [collectionEl, productsEl].filter(Boolean);
        }

        let active = inner.dataset.tenActiveTab;
        if (!tabs.some((t) => t.key === active)) active = tabs[0].key;

        const tabBar = existingBar || document.createElement('div');
        tabBar.className = 'ten-search-tabs';
        tabBar.setAttribute('role', 'tablist');
        tabBar.innerHTML = '';
        tabs.forEach((t) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ten-search-tab';
          btn.textContent = t.label;
          btn.dataset.tab = t.key;
          btn.setAttribute('role', 'tab');
          btn.addEventListener('click', () => activateTab(inner, t.key));
          tabBar.appendChild(btn);
        });
        inner.insertBefore(tabBar, tabs[0].els[0]);

        activateTab(inner, active);
      } finally {
        observer.observe(content, { childList: true, subtree: true });
      }
    }

    observer.observe(content, { childList: true, subtree: true });
    organize();
  }

  /**
   * Makes two kinds of link run an instant search instead of a full page
   * navigation, matching the pre-4.1.4 ten-search behaviour:
   *   1. The "Popular searches" links in the default panel
   *      (snippets/ten-search-default-panel.liquid).
   *   2. Shopify's own native query-suggestion pills (the "sheepskin dog
   *      toy" / "sheepskin" pill row above the tabs once you've typed a few
   *      characters — predictive-search-results__pill). Chris Nelis-Rouse —
   *      these render as plain <a href="/search?q=..."> like the default
   *      panel links, so clicking one left the dropdown entirely and loaded
   *      the full /search page instead of refining the results in place.
   *      Both share the exact same href shape, so one handler covers both.
   * "Shop by category" / "Guides & advice" links are left to navigate
   * normally since they point at real collection/page URLs.
   */
  function setupInstantSearchLinks(modal, input) {
    const observer = new MutationObserver(wire);

    function wire() {
      modal.querySelectorAll('#ten-search-default .ten-search-default__link, .predictive-search-results__pill').forEach((link) => {
        if (link.dataset.tenWired) return;
        const href = link.getAttribute('href') || '';
        if (!href.includes('/search?q=')) return;
        link.dataset.tenWired = '1';
        link.addEventListener('click', (e) => {
          // The pill lives inside predictive-search-component's own results
          // markup, which has its own click listener (also registered on
          // the results container, also in the capture phase) that clears
          // the query and closes the dialog on any link click, anticipating
          // a real page navigation. Plain preventDefault()/stopPropagation()
          // in the bubble phase stops the navigation but runs AFTER that
          // native listener has already fired — the query got typed in and
          // then immediately wiped out from under it. Registering this in
          // the capture phase (see addEventListener call below) makes ours
          // run first on the way down, and stopImmediatePropagation stops
          // the native listener on this same element from running at all.
          // The default panel links don't strictly need this — they're the
          // static empty-state template, outside that delegation — but it's
          // harmless there too, so one handler covers both cleanly.
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const params = new URLSearchParams(href.split('?')[1] || '');
          const q = params.get('q') || link.textContent.trim();
          input.value = q;
          input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: q }));
          input.focus();
        }, true);
      });
    }

    observer.observe(modal, { childList: true, subtree: true });
    wire();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
